import { APP_URL, GMAIL_PRB_CLIENT_ID, GMAIL_PRB_LISTING_URL, OFFICE_PHISH_REPORT_CODE } from "config";
import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import {
    Box,
    Button,
    Grid,
    Snackbar,
    Switch,
    TextField,
    Typography,
    Tabs,
    Tab,
    Alert,
    IconButton,
    Link,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GoogleLogo from "assets/images/icons/google.svg";
import MicrosoftLogo from "assets/images/icons/microsoft.svg";
import { useUpdateGoogleWorkspaceDomainMutation, useUpdateTenancyMutation } from "../../store/services/tenancy.service";
import { useLazyTestGoogleWorkspaceConnectionQuery } from "../../store/services/simulation-service";
import { useSelector } from "react-redux";
import MainCard from "../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from 'react-router-dom';
import { getCardColor } from "../../utils/colorUtils";
import PhishReportCustomisation from "./PhishReportCustomisation";

PhishReportButton.propTypes = {
    data: PropTypes.object.isRequired
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`reports-tabpanel-${index}`}
            aria-labelledby={`reports-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const GMAIL_PRB_SCOPE = "https://www.googleapis.com/auth/gmail.modify";

function CopyableValue({ label, value }) {
    return (
        <Box mt={1} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField size="small" fullWidth label={label} value={value} InputProps={{ readOnly: true }} />
            <Tooltip title={`Copy ${label}`}>
                <IconButton aria-label={`Copy ${label}`} onClick={() => navigator.clipboard.writeText(value)}>
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

CopyableValue.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
};

// Domain-wide delegation can't be granted through a consent redirect like Microsoft's, so the
// admin pastes our client ID and scope into the Admin console, then tells us their domain.
function GoogleWorkspaceCard({ tenancyId, connectedDomain, onResult, sx }) {
    const [domain, setDomain] = useState(connectedDomain || "");
    const [updateDomain, { isLoading }] = useUpdateGoogleWorkspaceDomainMutation();
    const [testConnection, { isFetching: isTesting }] = useLazyTestGoogleWorkspaceConnectionQuery();

    const test = () => {
        testConnection({ tenancyId })
            .unwrap()
            .then((result) => onResult(result.message, result.ok ? "success" : "warning"))
            .catch(() => onResult("Couldn't check the connection. Try again in a few minutes.", "error"));
    };

    const save = (value) => {
        updateDomain({ tenancyId, domain: value })
            .unwrap()
            .then((result) => {
                setDomain(result?.googleWorkspaceDomain || "");
                onResult(value ? "Google Workspace domain connected." : "Google Workspace domain disconnected.", "success");
            })
            .catch((message) => onResult(message, "error"));
    };

    return (
        <MainCard content={true} boxShadow={true} border={false} sx={sx}>
            <Typography variant="h4">Google Workspace</Typography>
            <Box mb={2}>
                <Typography variant="caption">
                    Lets the Gmail Phish Report Button move reported emails to Bin and forward them as .eml. A Google Workspace super admin adds this delegation once, then enters your domain below.
                </Typography>
            </Box>

            {GMAIL_PRB_CLIENT_ID ? (
                <>
                    <CopyableValue label="Client ID" value={GMAIL_PRB_CLIENT_ID} />
                    <CopyableValue label="OAuth scope" value={GMAIL_PRB_SCOPE} />
                    <Box mt={1} mb={3}>
                        <Link href="https://admin.google.com/ac/owl/domainwidedelegation" target="_blank" rel="noopener noreferrer">
                            Open domain-wide delegation in the Google Admin console
                        </Link>
                    </Box>
                </>
            ) : (
                <Alert severity="info" sx={{ mb: 3 }}>Gmail isn&apos;t available on this environment yet.</Alert>
            )}

            <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    label="Google Workspace domain"
                    placeholder="example.com"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                />
                <Button variant="contained" color="warning" disabled={isLoading || !domain.trim()} onClick={() => save(domain)}>
                    Connect
                </Button>
                {connectedDomain &&
                    <Button variant="outlined" color="warning" disabled={isLoading} onClick={() => save("")}>
                        Disconnect
                    </Button>
                }
            </Box>
            {connectedDomain &&
                <Box mt={1} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography variant="caption">Connected: {connectedDomain}</Typography>
                    <Button size="small" variant="text" color="warning" disabled={isTesting} onClick={test}>
                        {isTesting ? "Testing..." : "Test connection"}
                    </Button>
                </Box>
            }
        </MainCard>
    );
}

GoogleWorkspaceCard.propTypes = {
    tenancyId: PropTypes.number.isRequired,
    connectedDomain: PropTypes.string,
    onResult: PropTypes.func.isRequired,
    sx: PropTypes.object
};

// ponytail: no Gmail help articles yet. Swap this for an iframe like the Outlook ones once Support publishes them.
const GMAIL_GUIDE_COMING_SOON = <Alert severity="info">The Gmail guide is coming soon. Contact support if you need help in the meantime.</Alert>;

// The help article for an Install or Update sub-tab: the Outlook one, or for now nothing on Google.
const helpArticle = (title, outlookUrl, isGoogle) => (isGoogle ? GMAIL_GUIDE_COMING_SOON : (
    <iframe style={{ width: "100%", height: "80vh", border: "none" }} title={title} src={outlookUrl} />
));

// Same URL the add-on gets from addontheme: the portal serves Logo paths itself.
const portalLogoUrl = (logo) => {
    if (!logo) {
        return null;
    }
    return logo.startsWith("https://") ? logo : `${APP_URL}/${logo.replace(/^\//, "")}`;
};

// A sub-tab shows when its feature flag is on, and the Google-only ones only on the Google side.
const isTabVisible = (tab, featureSwitches, isGoogle) => {
    const flagOn = !tab.featureFlag || Boolean(featureSwitches && featureSwitches[tab.featureFlag]);
    return flagOn && (!tab.googleOnly || isGoogle);
};

function ExchangePermissionsCard({ onGrant, isProcessing, sx }) {
    return (
        <MainCard content={true} boxShadow={true} border={false} sx={sx}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <Typography variant="h4">
                    Exchange Permissions
                </Typography>
            </Box>
            <Box mb={3}>
                <Typography variant="caption">
                    Grant permissions to allow reading and editing of email. This is to enable the function of both email forwarding and deletion, post report.
                </Typography>
            </Box>
            <Box justifyContent="center" display="flex" flexDirection="column" alignItems="center">
                <Button
                    onClick={onGrant}
                    variant="contained"
                    color="warning"
                    sx={{ width: '80%' }}
                    disabled={isProcessing}
                >
                    {isProcessing ? 'Processing...' : 'Grant permissions in Microsoft'}
                </Button>
            </Box>
        </MainCard>
    );
}

ExchangePermissionsCard.propTypes = {
    onGrant: PropTypes.func.isRequired,
    isProcessing: PropTypes.bool,
    sx: PropTypes.object
};

// Google has no install-by-manifest like Microsoft 365: a Workspace super admin installs the add-on
// for the domain from our unlisted Marketplace listing, so all we can do is link to it.
function GmailAddOnCard({ onResult, sx }) {
    const copyLink = () => {
        navigator.clipboard.writeText(GMAIL_PRB_LISTING_URL);
        onResult("Install link copied.", "success");
    };

    return (
        <MainCard content={true} boxShadow={true} border={false} sx={sx}>
            <Typography variant="h4">Gmail add-on</Typography>
            <Box mb={2}>
                <Typography variant="caption">
                    A Google Workspace super admin installs the Phish Report add-on for everyone in your domain from the Google Workspace Marketplace. Share the link if that&apos;s someone else.
                </Typography>
            </Box>
            {GMAIL_PRB_LISTING_URL ? (
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="contained" color="warning" href={GMAIL_PRB_LISTING_URL} target="_blank" rel="noopener noreferrer">
                        Install from Workspace Marketplace
                    </Button>
                    <Button variant="outlined" color="warning" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copyLink}>
                        Copy link
                    </Button>
                </Box>
            ) : (
                <Alert severity="info">The add-on isn&apos;t in the Google Workspace Marketplace yet.</Alert>
            )}
        </MainCard>
    );
}

GmailAddOnCard.propTypes = {
    onResult: PropTypes.func.isRequired,
    sx: PropTypes.object
};

function a11yProps(index) {
    return {
        id: `reports-tab-${index}`,
        'aria-controls': `reports-tabpanel-${index}`,
    };
}

export default function PhishReportButton(props) {
    const { data } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';
    const [subTabValue, setSubTabValue] = useState(0);

    const [tenancyData, setTenancyData] = useState(data);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [isProcessingCallback, setIsProcessingCallback] = useState(false);

    // Get tenancy ID and feature switches
    const { tenancyId, featureSwitches } = useSelector((state) => state.tenancy);

    const [updateTenancy] = useUpdateTenancyMutation();

    // Microsoft 365 or Google Workspace. Only Gmail tenancies get the choice; it opens on Google
    // once a Workspace domain is connected. Forward Phish is shared: one setting for both.
    // For now the Google side is Boxphish staff (super admins) only, even with the flag on.
    const { role } = useSelector((state) => state.auth?.user || {});
    const gmailEnabled = Boolean(featureSwitches?.enableGmailPhishReportButton) && role === "Administrators";
    const [platform, setPlatform] = useState(data.googleWorkspaceDomain ? "google" : "microsoft");
    const isGoogle = gmailEnabled && platform === "google";

    const handlePlatformChange = (event, value) => {
        if (value) {
            setPlatform(value);
            setSubTabValue(0);
        }
    };

    const startPhishReportAuth = () => {
        window.open(
            `https://login.microsoftonline.com/common/adminconsent?client_id=${OFFICE_PHISH_REPORT_CODE}&redirect_uri=${APP_URL}/outlookPluginHandler&state=${data.tenancyId}`,
            "_blank"
        );
    };

    const handleSubTabChange = (event, newValue) => {
        setSubTabValue(newValue);
    };

    const handleSave = () => {
        // unwrap(): RTK Query resolves failed mutations with { error } instead of rejecting, so
        // without it a failed save showed the success message and the catch never ran.
        updateTenancy({ data: tenancyData, tenancyId: tenancyData.tenancyId })
            .unwrap()
            .then(() => {
                setSnackbarMessage("Phish report button settings updated successfully.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);
            })
            .catch((error) => {
                setSnackbarMessage("Error updating phish report button settings.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                console.error("Error updating phish report button settings:", error);
            });
    };

    const showResult = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleForwardPhish = () => {
        setTenancyData(prevData => ({ ...prevData, forwardPhish: !prevData.forwardPhish }));
    };

    const handleForwardAsEml = () => {
        setTenancyData(prevData => ({ ...prevData, enableReportEmlForwarding: !prevData.enableReportEmlForwarding }));
    };

    // Define all possible tabs with their feature flags
    const allTabs = [
        { 
            label: "Settings", 
            component: (
                <Grid container spacing={2} xs={12}>
                    <Grid item xs={6}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: getCardColor(lightMode) }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <Typography variant="h4">
                                    Forward Phish Emails
                                </Typography>

                                <Switch
                                    color="primary"
                                    checked={tenancyData.forwardPhish}
                                    onChange={handleForwardPhish}
                                />
                            </Box>
                            <Typography variant="caption">
                                Should non-system emails be forwarded to an inbox if a user marks them as Spam?
                            </Typography>

                            {tenancyData.forwardPhish &&
                                <Box mt={4} mb={1}>
                                    <TextField
                                        variant={"outlined"}
                                        fullWidth
                                        label="Send Email to"
                                        InputLabelProps={{ style: { backgroundColor: "transparent" } }}
                                        type={"email"}
                                        value={tenancyData.reporterEmail}
                                        onChange={(e) => setTenancyData(prevData => ({ ...prevData, reporterEmail: e.target.value }))}
                                    />
                                </Box>
                            }

                            {/* Only once tenancy-service returns the setting: before that backend is
                                released, a Save silently drops it and the switch flips back to off. */}
                            {tenancyData.forwardPhish && data.enableReportEmlForwarding !== undefined &&
                                <Box mt={3}>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                        <Typography variant="h5">
                                            Forward as the original email (.eml)
                                        </Typography>

                                        <Switch
                                            color="primary"
                                            checked={Boolean(tenancyData.enableReportEmlForwarding)}
                                            onChange={handleForwardAsEml}
                                        />
                                    </Box>
                                    <Typography variant="caption">
                                        Send your security team the reported email as an .eml, with its attachments and links intact, instead of a text copy. Needs Exchange Permissions granted (or Google Workspace delegation for Gmail).
                                    </Typography>
                                </Box>
                            }
                        </MainCard>
                    </Grid>

                    {isGoogle ? (
                        <>
                            <Grid item xs={6}>
                                <GoogleWorkspaceCard
                                    tenancyId={data.tenancyId}
                                    connectedDomain={data.googleWorkspaceDomain}
                                    onResult={showResult}
                                    sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <GmailAddOnCard onResult={showResult} sx={{ bgcolor: getCardColor(lightMode) }} />
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={6}>
                            <ExchangePermissionsCard
                                onGrant={startPhishReportAuth}
                                isProcessing={isProcessingCallback}
                                sx={{ height: "100%", bgcolor: getCardColor(lightMode) }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            color="warning"
                            sx={{ ml: 2, width: "10%" }}
                            disabled={isProcessingCallback}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>
            ),
            featureFlag: 'enableSettingsPhishReportButton'
        },
        {
            label: "Customisation",
            component: (
                <PhishReportCustomisation
                    data={data}
                    logoUrl={portalLogoUrl(data.logo)}
                    onResult={showResult}
                />
            ),
            featureFlag: 'enableGmailPhishReportButton',
            googleOnly: true
        },
        {
            label: "Install",
            component: helpArticle("install", 'https://boxphishsupport.helpdocs.io/article/dg65nxlbhu-phish-report-button-outlook-direct-install', isGoogle)
        },
        {
            label: "Update",
            component: helpArticle("update", 'https://boxphishsupport.helpdocs.io/article/o6c2b9lhuo-updating-phish-report-button', isGoogle)
        }
    ];

    const visibleTabs = allTabs.filter(tab => isTabVisible(tab, featureSwitches, isGoogle));

    return (
        <>
            <Box sx={{ width: '100%' }}>
                {gmailEnabled &&
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, pb: 2 }}>
                        {/* Figma's segmented control: a grey track with a white pill. Fixed colours, because the
                            theme's ToggleButton overrides follow tenancy branding, which can be white. */}
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={platform}
                            onChange={handlePlatformChange}
                            aria-label="email platform"
                            sx={{
                                bgcolor: '#F1F3F4',
                                borderRadius: 2,
                                p: 0.5,
                                '& .MuiToggleButtonGroup-grouped.MuiToggleButton-root': {
                                    flex: 'none',
                                    m: 0,
                                    gap: 1,
                                    px: 2,
                                    border: 0,
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    backgroundColor: 'transparent !important',
                                    color: '#5F6368 !important'
                                },
                                '& .MuiToggleButtonGroup-grouped.MuiToggleButton-root.Mui-selected': {
                                    backgroundColor: '#FFFFFF !important',
                                    color: '#202124 !important',
                                    boxShadow: '0 1px 2px rgba(60, 64, 67, 0.3)'
                                }
                            }}
                        >
                            <ToggleButton value="microsoft"><img src={MicrosoftLogo} alt="" width={16} height={16} />Microsoft 365</ToggleButton>
                            <ToggleButton value="google"><img src={GoogleLogo} alt="" width={16} height={16} />Google Workspace</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption">Choose the email platform to configure the Phish Report Button for.</Typography>
                    </Box>
                }
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={subTabValue} onChange={handleSubTabChange} aria-label="phish report tabs">
                        {visibleTabs.map((tab, index) => (
                            <Tab key={index} label={tab.label} {...a11yProps(index)} />
                        ))}
                    </Tabs>
                </Box>
                {visibleTabs.map((tab, index) => (
                    <TabPanel key={index} value={subTabValue} index={index}>
                        {tab.component}
                    </TabPanel>
                ))}
            </Box>
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                       sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}